/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import { CosmosText, EdsIcon, ResponsiveContainer, SectionContainer } from "@components/elements/atoms";
import { CosmosDesc } from "@components/elements/molecules/title-desc-stack";
import { IndustryFragment, NewsletterSignupFragment } from "@cosmos/hygraph-schema";
import { css } from "@panda/css";
import { VStack } from "@panda/jsx";
import { IndustrySelector } from "./IndustrySelector";
import { NewsletterSignupButton } from "./NewsletterSignup.button";
import { useEffect, useState } from "react";
import { useLocale, useMessages } from "@epic-mw/i18n";
import { useAccountAODCPolicy, useIsOptIn, useSaveIndustry, useUpdateOptIn } from "@cosmos/next-hooks";
import { useLocation } from "@epic-mw/app-context";

const styles = css({
	maxWidth: "860px",
	marginInline: "auto",
	textAlign: "center",
});

export const NewsletterSignup = ({
	signUpLabel,
	industryPlaceholder,
	industriesRef,
	successTitle,
	successDescription,
}: NewsletterSignupFragment): React.JSX.Element => {
	const [submit, setSubmit] = useState(false);
	const location = useLocation();

	useEffect(() => {
		if (location.hash === "#newsletter-signup") {
			setTimeout(() => {
				document.getElementById("newsletter-signup")?.scrollIntoView();
			}, 250);
		}
	}, [location]);

	let showIndustry = false;
	if (industryPlaceholder && industriesRef?.industries.length) {
		showIndustry = true;
	}
	const paramsSubscribe = location.searchParams.get("subscribe") || "";
	let initIndustry = null;
	if (paramsSubscribe && showIndustry) {
		const match = industriesRef?.industries.find((ind) => {
			return ind.salesForceId === paramsSubscribe;
		});
		if (match && match.salesForceId) {
			initIndustry = match;
		}
	}

	const [selectedIndustry, setSelectedIndustry] = useState<IndustryFragment | null>(initIndustry);
	const locale = useLocale();
	const { hasOptIn } = useIsOptIn();
	const { isOptInSuccess, isLoading: isLoadingOptIn, error: isOptInFailed } = useUpdateOptIn(submit);
	const { isLoading: isLoadingDataRouter } = useSaveIndustry(
		{
			industry_v1: selectedIndustry?.salesForceId,
			industry_v2: selectedIndustry?.dataRouterId,
		},
		submit,
	);
	const isLoading = isLoadingOptIn || isLoadingDataRouter;

	const { result: accountPolicy } = useAccountAODCPolicy();

	const isAodcFailed = accountPolicy?.AODCFailed || false;

	const validIndustrySelected = Boolean(showIndustry && selectedIndustry?.salesForceId);
	const messages = useMessages();

	const notAllowed = messages["epic.cosmos.newsletter.not.allowed.title"] || "";
	const nowAllowedDesc = messages["epic.cosmos.newsletter.not.allowed.message"] || "";
	const failedDesc = messages["epic.cosmos.newsletter.failed.message"] || "";
	const optInDesc = messages["epic.cosmos.newsletter.globaloptin"] || "";
	const privacyPolicyLabel = messages["epic.cosmos.privacypolicy"] || "";
	const privacyPolicyHtml = `${optInDesc || ""}  <a href="https://www.epicgames.com/site/${locale}/privacypolicy">${privacyPolicyLabel}</a>`;

	const signupLayout = (
		<>
			<CosmosText variant={"headingXL"}>
				{signUpLabel}{" "}
				{showIndustry ? (
					<IndustrySelector
						industryPlaceholder={industryPlaceholder}
						industries={industriesRef?.industries || []}
						selectedIndustry={selectedIndustry}
						setSelectedIndustry={setSelectedIndustry}
						validIndustrySelected={validIndustrySelected}
					/>
				) : null}
			</CosmosText>

			<NewsletterSignupButton
				showIndustry={showIndustry}
				isLoading={isLoading}
				selectedIndustry={selectedIndustry}
				setSubmit={setSubmit}
				disabled={!validIndustrySelected}
			/>

			{!isLoading ? (
				<CosmosDesc className={css({ maxWidth: "460px" })} description={{ html: privacyPolicyHtml, variant: "uiSM" }} />
			) : null}
		</>
	);

	const aodcLayout = (
		<>
			<CosmosText variant={"headingXL"}>{notAllowed}</CosmosText>
			<CosmosText variant="uiMD" text={nowAllowedDesc} color="eds.text.secondary" />
		</>
	);

	const alreadyOptInLayout = (
		<>
			<CosmosText variant={"headingXL"}>{successTitle}</CosmosText>
			<EdsIcon icon="Check" gradient="blue" size="lg" />
		</>
	);

	const successOptInLayout = (
		<>
			<CosmosText variant={"headingXL"}>{successTitle}</CosmosText>
			<CosmosDesc className={css({ maxWidth: "460px" })} description={{ html: successDescription }} />
			<EdsIcon icon="Check" gradient="blue" size="lg" />
		</>
	);

	const errorOptInLayout = (
		<>
			<CosmosText variant={"headingXL"}>{notAllowed}</CosmosText>
			<CosmosDesc className={css({ maxWidth: "460px" })} description={{ html: failedDesc }} />
		</>
	);

	let content = signupLayout;
	if (isAodcFailed) {
		content = aodcLayout;
	} else if (isOptInSuccess) {
		content = successOptInLayout;
	} else if (hasOptIn) {
		content = alreadyOptInLayout;
	} else if (isOptInFailed) {
		content = errorOptInLayout;
	}
	return (
		<ResponsiveContainer id="newsletter-signup">
			<SectionContainer>
				<VStack alignItems="center" gap="eds.dimension.24" className={`newsletter-signup-section ${styles}`}>
					<EdsIcon icon="Envelope" gradient="blue" size="xxl" />
					{content}
				</VStack>
			</SectionContainer>
		</ResponsiveContainer>
	);
};
